import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Mail, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";

const blankStep = () => ({ delay_hours: 24, subject: "", body_html: "" });

function SequenceCard({ seq, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(seq.name);
  const [active, setActive] = useState(seq.active);
  const [steps, setSteps] = useState(seq.steps);

  const save = async () => {
    try {
      await api.put(`/admin/sequences/${seq.id}`, { name, trigger: "lead_capture", active, steps });
      toast.success("Sequence saved");
      onSaved();
    } catch { toast.error("Save failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this sequence?")) return;
    await api.delete(`/admin/sequences/${seq.id}`); onSaved();
  };
  const setStep = (i, k, v) => setSteps(steps.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  return (
    <div className="brut-card p-5" data-testid={`sequence-${seq.id.slice(0, 8)}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-bubble border-2 border-ink"><Mail className="w-5 h-5 text-white" /></span>
          <div>
            <div className="font-display font-bold text-lg">{seq.name}</div>
            <div className="text-sm text-ink/60">{seq.steps.length} emails · {seq.active_enrollments || 0} active enrollments</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`tag-pill ${active ? "bg-green-200" : "bg-slate-200"}`}>{active ? "Active" : "Off"}</span>
          <button onClick={() => setOpen(!open)} className="brut-btn !py-2 !px-4 bg-white text-sm" data-testid={`sequence-toggle-${seq.id.slice(0, 8)}`}>
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} {open ? "Close" : "Edit"}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-5 space-y-4 border-t-2 border-dashed border-ink/20 pt-4">
          <div className="flex flex-wrap gap-3 items-center">
            <input className="brut-input flex-1 min-w-[200px]" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sequence name" />
            <label className="flex items-center gap-2 font-semibold text-sm">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid={`sequence-active-${seq.id.slice(0, 8)}`} />
              Active (only one per trigger)
            </label>
          </div>
          {steps.map((s, i) => (
            <div key={i} className="brut border-ink bg-white p-4 shadow-brut">
              <div className="flex items-center justify-between mb-2">
                <span className="font-display font-bold">Email {i + 1}</span>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-ink/60">Send after</span>
                  <input type="number" min="0" className="brut-input !py-1 !w-20 text-sm" value={s.delay_hours}
                    onChange={(e) => setStep(i, "delay_hours", parseInt(e.target.value || "0"))} />
                  <span className="text-ink/60">hrs</span>
                  <button onClick={() => setSteps(steps.filter((_, idx) => idx !== i))} className="text-tangerine"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <input className="brut-input mb-2" placeholder="Subject line" value={s.subject} onChange={(e) => setStep(i, "subject", e.target.value)} />
              <textarea className="brut-input min-h-[110px] font-mono text-xs" placeholder="Email HTML body ({{name}} is replaced with the lead's first name)"
                value={s.body_html} onChange={(e) => setStep(i, "body_html", e.target.value)} />
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSteps([...steps, blankStep()])} className="brut-btn !py-2 !px-4 bg-sky text-sm"><Plus className="w-4 h-4" /> Add email</button>
            <button onClick={save} className="brut-btn !py-2 !px-4 bg-green-300 text-sm" data-testid={`sequence-save-${seq.id.slice(0, 8)}`}><Save className="w-4 h-4" /> Save sequence</button>
            <button onClick={del} className="brut-btn !py-2 !px-4 bg-white text-tangerine text-sm ml-auto"><Trash2 className="w-4 h-4" /> Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FlowsTab() {
  const [seqs, setSeqs] = useState([]);
  const load = () => api.get("/admin/sequences").then((r) => setSeqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/sequences", {
        name: "New Sequence", trigger: "lead_capture", active: false,
        steps: [{ delay_hours: 0, subject: "Welcome!", body_html: "<p>Hi {{name}}, thanks for joining!</p>" }],
      });
      toast.success("Sequence created"); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-flows">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl mb-1">Email Flows</h1>
          <p className="text-ink/60">Automated drip sequences triggered when a lead signs up.</p>
        </div>
        <button onClick={create} className="brut-btn !py-2 !px-4 bg-bubble text-white text-sm" data-testid="create-sequence"><Plus className="w-4 h-4" /> New sequence</button>
      </div>
      <div className="space-y-4">
        {seqs.length === 0 && <div className="brut-card p-6 text-ink/60">No sequences yet.</div>}
        {seqs.map((s) => <SequenceCard key={s.id} seq={s} onSaved={load} />)}
      </div>
    </div>
  );
}
